import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, UserPlus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { head: 'Διαχειριστής', lawyer: 'Δικηγόρος', secretary: 'Γραμματεία' };

export const OfficeTeamModal = ({ open, onClose }) => {
  const [office, setOffice] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'lawyer' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/office`);
      setOffice(data);
    } catch (e) {
      toast.error('Αδυναμία φόρτωσης ομάδας');
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addMember = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error('Συμπληρώστε όνομα, email και κωδικό (τουλάχιστον 8 χαρακτήρες)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/office/members`, form);
      toast.success(`Το μέλος «${form.name}» προστέθηκε στο γραφείο`);
      setForm({ name: '', email: '', password: '', role: 'lawyer' });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Σφάλμα προσθήκης μέλους');
    }
    setSaving(false);
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API}/office/members/${m.id}`, { active: !m.active });
      toast.success(m.active ? `Ο λογαριασμός «${m.name}» απενεργοποιήθηκε` : `Ο λογαριασμός «${m.name}» ενεργοποιήθηκε`);
      load();
    } catch (e) {
      toast.error('Σφάλμα ενημέρωσης μέλους');
    }
  };

  const isHead = office?.my_role === 'head';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="office-team-modal" className="max-w-lg max-h-[90vh] overflow-y-auto sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-xl font-semibold text-slate-900 text-left flex items-center gap-2.5">
            <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            Ομάδα γραφείου
          </DialogTitle>
        </DialogHeader>

        {!office ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5 mt-2">
            <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
              {office.members.map((m) => (
                <div key={m.id} data-testid={`office-member-${m.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold truncate ${m.active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                      {m.name || m.email}{m.id === office.my_id ? ' (εσείς)' : ''}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      m.role === 'head' ? 'bg-red-100 text-red-700'
                        : m.role === 'lawyer' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {ROLE_LABELS[m.role] || m.role}
                    </span>
                    {isHead && m.id !== office.my_id && (
                      <button
                        data-testid={`office-member-toggle-${m.id}`}
                        onClick={() => toggleActive(m)}
                        className={`text-xs font-medium px-2 py-1 rounded-md border transition-colors ${
                          m.active
                            ? 'border-slate-300 text-slate-500 hover:bg-slate-50'
                            : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                        }`}
                      >
                        {m.active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {isHead && (
              <div className="border border-slate-200 rounded-xl p-3 sm:p-4 space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <UserPlus className="w-4 h-4 text-red-700" />
                  Προσθήκη μέλους
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input data-testid="office-member-name-input" placeholder="Ονοματεπώνυμο" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" />
                  <Input data-testid="office-member-email-input" type="email" placeholder="Email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" />
                  <Input data-testid="office-member-password-input" type="password" placeholder="Κωδικός (min 8)" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-10" />
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="office-member-role-select" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lawyer">Δικηγόρος</SelectItem>
                      <SelectItem value="secretary">Γραμματεία</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-slate-400" style={{ lineHeight: 1.4 }}>
                  Ο δικηγόρος βλέπει μόνο τις υποθέσεις που του χρεώνετε. Η γραμματεία καταχωρεί εγγραφές και βλέπει τα προγράμματα, χωρίς πρόσβαση στα οικονομικά.
                </p>
                <button data-testid="office-member-add-btn" onClick={addMember} disabled={saving}
                  className="w-full h-10 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-60 flex items-center justify-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Προσθήκη στο γραφείο
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OfficeTeamModal;
